import type { ReactNode } from 'react';
import {
  coerceBhConnectionCount,
  coerceBhConnectionPercent,
  isWarehouseWhoWereReachingSnapshot,
  type ConnectionsBhCareTrendPoint,
  type ConnectionsBhSankeyNode,
} from '@act/shared/data-transfer-objects';
import { CartesianGrid, LabelList, Line, LineChart, Tooltip, XAxis, YAxis } from 'recharts';
import SectionReveal from './SectionReveal';
import SectionBanner from './SectionBanner';
import { HostedReportSectionLayout } from '../HostedReportSectionLayout';
import { ReportMockFieldFrame } from '../ReportMockFieldFrame';
import type { ReportHostedSectionProps } from '../reportHostedSection.types';
import { useReportChartDimensions } from '../../hooks/useReportChartDimensions.hook';
import { useReportsPrintStaticLayout } from '../hooks/useReportsPrintStaticLayout.hook';
import BhConnectionsSankey from './BhConnectionsSankey';
import { body, CORAL, display, foreground, muted } from './tokens';

const BH_TREND_CHART_HEIGHT = 280;

const cardStyle = { background: '#fff', border: '1px solid var(--reema-midnight-10)' } as const;
const DISCONNECTED_BAR_MAX_WIDTH = 820;
const REEMA_CLINICIAN_GREEN = '#3E7A6B';

const disconnectedBarWidthPct = (value: number, total: number): number =>
  total > 0 ? (value / total) * 100 : 0;

const disconnectedSharePct = (value: number, total: number): number =>
  Math.round(disconnectedBarWidthPct(value, total));

const readTrend = (data: Record<string, unknown>): ConnectionsBhCareTrendPoint[] => {
  if (!Array.isArray(data.bhCareTrend)) {
    return [];
  }
  return data.bhCareTrend as ConnectionsBhCareTrendPoint[];
};

const readSankeyNodes = (data: Record<string, unknown>): ConnectionsBhSankeyNode[] => {
  if (!Array.isArray(data.sankeyNodes)) {
    return [];
  }
  return data.sankeyNodes as ConnectionsBhSankeyNode[];
};

interface BhTrendXAxisTickProps {
  x?: number;
  y?: number;
  payload?: { value: string };
  index?: number;
}

/**
 * Anchors the first and last month labels inward so long labels do not clip at
 * the chart edges in print.
 * @param lastIndex - Index of the final trend point.
 * @returns Recharts X-axis tick renderer.
 */
const createBhTrendXAxisTick =
  (lastIndex: number) =>
  ({ x = 0, y = 0, payload, index = 0 }: BhTrendXAxisTickProps) => {
    const textAnchor =
      index === 0 ? 'start' : index === lastIndex ? 'end' : 'middle';

    return (
      <text
        x={x}
        y={y}
        dy={16}
        fill={muted}
        fontSize={11}
        textAnchor={textAnchor}
      >
        {payload?.value ?? ''}
      </text>
    );
  };

export interface ConnectionsBehavioralCarePageThreeProps {
  /** Resolved section snapshot. */
  data: Record<string, unknown>;
  /** Optional callout/insight tail (rendered as direct children of the page-three shell). */
  tail?: ReactNode;
}

/**
 * Page-three print shell: disconnected card, white flex gap, and optional insight tail.
 * @param props - Snapshot data and optional tail blocks from {@link ReportSectionItemView}.
 */
export const ConnectionsBehavioralCarePageThree = ({
  data,
  tail,
}: ConnectionsBehavioralCarePageThreeProps) => {
  const noPreEng = coerceBhConnectionCount(data.noPreEngBhVisit);
  const eligibleConnected = coerceBhConnectionCount(data.eligibleCostBhConnected);
  const eligiblePct = coerceBhConnectionPercent(data.eligibleCostBhPct);
  const eligibleReema = coerceBhConnectionCount(data.eligibleCostBhReema);

  if (noPreEng <= 0 && tail == null) {
    return null;
  }

  return (
    <div className="connections-bh-page-three-tail">
      <div className="connections-bh-page-three-body max-w-5xl mx-auto px-6 w-full">
        {noPreEng > 0 ? (
          <ReportMockFieldFrame data={data} field="noPreEngBhVisit" className="mb-10">
            <div className="connections-bh-disconnected-card rounded-2xl p-6 md:p-10 shadow-sm" style={cardStyle}>
              <h3
                style={{
                  fontFamily: display,
                  fontWeight: 600,
                  fontSize: '28px',
                  color: foreground,
                  margin: '0 0 0.25rem',
                  lineHeight: 1.2,
                }}
              >
                From disconnected to in care
              </h3>
              <p
                className="mb-8"
                style={{
                  fontFamily: body,
                  fontSize: '13px',
                  color: muted,
                  margin: '0 0 2rem',
                  lineHeight: 1.5,
                }}
              >
                Engaged members with no behavioral health visit in the prior 12 months, and the
                share now in care.
              </p>
              <div className="space-y-8">
                <div>
                  <div className="stat-num stat-num--lg">
                    {noPreEng.toLocaleString()}
                  </div>
                  <div className="text-sm" style={{ color: muted, marginTop: 8 }}>
                    Engaged members with no BH visit in last 12 months
                  </div>
                </div>
                <div>
                  <div className="text-sm mb-2" style={{ color: muted }}>
                    Now in behavioral health care after Reema engagement
                  </div>
                  <div className="flex items-center gap-3">
                    <div style={{ width: '100%', maxWidth: DISCONNECTED_BAR_MAX_WIDTH }}>
                      <div
                        className="h-8 rounded"
                        style={{
                          width: `${disconnectedBarWidthPct(eligibleConnected, noPreEng)}%`,
                          background: CORAL,
                          minWidth: eligibleConnected > 0 ? 24 : 0,
                        }}
                      />
                    </div>
                    <div className="text-sm font-medium shrink-0" style={{ color: foreground }}>
                      {eligibleConnected.toLocaleString()} ({eligiblePct}%)
                    </div>
                  </div>
                </div>
                <div>
                  <div className="text-sm mb-2" style={{ color: muted }}>
                    Members connected to a Reema clinician
                  </div>
                  <div className="flex items-center gap-3">
                    <div style={{ width: '100%', maxWidth: DISCONNECTED_BAR_MAX_WIDTH }}>
                      <div
                        className="h-8 rounded"
                        style={{
                          width: `${disconnectedBarWidthPct(eligibleReema, noPreEng)}%`,
                          background: REEMA_CLINICIAN_GREEN,
                          minWidth: eligibleReema > 0 ? 24 : 0,
                        }}
                      />
                    </div>
                    <div className="text-sm font-medium shrink-0" style={{ color: foreground }}>
                      {disconnectedSharePct(eligibleReema, noPreEng)}%
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </ReportMockFieldFrame>
          ) : null}
      </div>
      {tail != null ? (
        <>
          <div className="connections-bh-page-three-flex-fill" aria-hidden="true" />
          {tail}
        </>
      ) : null}
    </div>
  );
};

/**
 * Connections to Behavioral Health Care: warehouse-backed BH connection metrics.
 * @param props - Resolved section snapshot.
 */
const ConnectionsBehavioralCare = ({
  data,
}: ReportHostedSectionProps) => {
  const printStatic = useReportsPrintStaticLayout();
  const { containerRef: bhTrendChartRef, dimensions: bhTrendChartDimensions } =
    useReportChartDimensions(printStatic ? 170 : BH_TREND_CHART_HEIGHT);
  const membersConnected = coerceBhConnectionCount(
    data.membersConnectedBh ?? data.connected,
  );
  const membersConnectedPct = coerceBhConnectionPercent(
    data.membersConnectedBhPct ?? data.connectionRate,
  );
  const avgDays = coerceBhConnectionCount(
    data.avgDaysToBhAppointment ?? data.avgDaysToConnect,
  );
  const noPreEng = coerceBhConnectionCount(data.noPreEngBhVisit);
  const eligibleConnected = coerceBhConnectionCount(data.eligibleCostBhConnected);
  const eligiblePct = coerceBhConnectionPercent(data.eligibleCostBhPct);
  const trend = readTrend(data);
  const sankeyNodes = readSankeyNodes(data);
  const isWarehouse = isWarehouseWhoWereReachingSnapshot(data);

  return (
    <HostedReportSectionLayout
      sectionDomId="connections-behavioral-care"
      className="section--white report-hosted-section-body"
      style={{ fontFamily: body }}
      banner={
        <SectionBanner
          title={
            <>
              Connections to
              <br />
              <span className="banner__title-line">Behavioral Health Care</span>
            </>
          }
          iconHref="#icon_behavioral_health"
        />
      }
    >
      <SectionReveal>
        <ReportMockFieldFrame data={data} field="membersConnectedBh">
          <div
            className="connections-bh-stat-grid stat-grid stat-grid--4"
            style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: '3rem' }}
          >
            <div className="stat-card stat-card--center">
              <div className="stat-num stat-num--md">
                {membersConnected.toLocaleString()}
              </div>
              <div className="stat-label">Members connected to BH care</div>
            </div>
            <div className="stat-card stat-card--center">
              <div className="stat-num stat-num--md">{membersConnectedPct}%</div>
              <div className="stat-label">Of engaged members receiving BH care</div>
            </div>
            <div className="stat-card stat-card--center">
              <div className="stat-num stat-num--md">{avgDays} days</div>
              <div className="stat-label">Average time to BH appointment</div>
            </div>
          </div>
        </ReportMockFieldFrame>

        {trend.length > 0 ? (
          <ReportMockFieldFrame data={data} field="bhCareTrend" className="mb-10">
            <div className="connections-bh-trend-card rounded-2xl p-6 shadow-sm" style={cardStyle}>
              <h3
                className="text-base font-semibold mb-1"
                style={{ color: foreground, fontFamily: "'Lora', serif" }}
              >
                Members engaged in behavioral health care
              </h3>
              <p className="text-sm mb-5" style={{ color: muted }}>
                Members currently in Reema BH care by month.
              </p>
              <div
                ref={bhTrendChartRef}
                className="connections-bh-trend-chart report-chart-host"
                style={{
                  width: '100%',
                  minWidth: 0,
                  height: printStatic ? 170 : BH_TREND_CHART_HEIGHT,
                }}
              >
                {bhTrendChartDimensions ? (
                  <LineChart
                    width={bhTrendChartDimensions.width}
                    height={bhTrendChartDimensions.height}
                    data={trend}
                    margin={
                      printStatic
                        ? { top: 20, right: 20, left: 8, bottom: 8 }
                        : { top: 24, right: 16, left: 0, bottom: 8 }
                    }
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#E8E0D6" vertical={false} />
                    <XAxis
                      dataKey="label"
                      scale="point"
                      padding={printStatic ? { left: 12, right: 12 } : { left: 0, right: 0 }}
                      tick={createBhTrendXAxisTick(trend.length - 1)}
                      axisLine={false}
                      tickLine={false}
                      height={printStatic ? 28 : 30}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: muted }}
                      width={printStatic ? 40 : 48}
                      axisLine={false}
                      tickLine={false}
                      domain={[0, 'auto']}
                    />
                    <Tooltip
                      contentStyle={{
                        borderRadius: 12,
                        border: '1px solid #E8E0D6',
                        fontSize: 13,
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke={CORAL}
                      strokeWidth={2.5}
                      dot={{ r: 5, fill: CORAL, strokeWidth: 0 }}
                      activeDot={{ r: 6 }}
                    >
                      <LabelList
                        dataKey="value"
                        position="top"
                        offset={12}
                        fontFamily={display}
                        style={{
                          fill: foreground,
                          fontSize: 12,
                          fontWeight: 700,
                          fontFamily: display,
                        }}
                      />
                    </Line>
                  </LineChart>
                ) : null}
              </div>
            </div>
          </ReportMockFieldFrame>
        ) : null}

        {sankeyNodes.length > 0 ? (
          <ReportMockFieldFrame
            data={data}
            field="sankeyNodes"
            className="connections-bh-sankey-block mb-10"
          >
            <div className="connections-bh-sankey-intro" style={{ marginBottom: '2.5rem' }}>
              <h3
                style={{
                  fontFamily: display,
                  fontWeight: 600,
                  fontSize: 'var(--reema-text-h2)',
                  color: foreground,
                  margin: '0 0 1rem',
                  lineHeight: 1.2,
                  letterSpacing: '-0.01em',
                }}
              >
                Two paths, one outcome.
              </h3>
              <p
                style={{
                  fontFamily: body,
                  fontSize: 'var(--reema-text-body-lg)',
                  color: muted,
                  maxWidth: '48rem',
                  margin: 0,
                  lineHeight: 1.6,
                }}
              >
                Members who&apos;ve had behavioral health care in the past are more likely to
                re-engage with a community in-network provider. Reema&apos;s telehealth therapy
                and medication management is the path forward for the harder-to-reach members
                who don&apos;t engage with community providers on their own.
              </p>
            </div>
            <BhConnectionsSankey nodes={sankeyNodes} />
          </ReportMockFieldFrame>
        ) : null}

        {noPreEng > 0 ? (
          <ReportMockFieldFrame
            data={data}
            field="noPreEngBhVisit"
            className="connections-bh-disconnected-intro mb-10"
          >
            <div className="connections-bh-print-page-lead" aria-hidden="true" />
            <div style={{ marginBottom: '2.5rem' }}>
              <h3
                style={{
                  fontFamily: display,
                  fontWeight: 600,
                  fontSize: 'var(--reema-text-h2)',
                  color: foreground,
                  margin: '0 0 1rem',
                  lineHeight: 1.2,
                  letterSpacing: '-0.01em',
                }}
              >
                The members who hadn&apos;t seen a behavioral health provider in over a year.
              </h3>
              <p
                style={{
                  fontFamily: body,
                  fontSize: 'var(--reema-text-body-lg)',
                  color: muted,
                  maxWidth: '48rem',
                  margin: 0,
                  lineHeight: 1.6,
                }}
              >
                {noPreEng.toLocaleString()} of the engaged members hadn&apos;t received any
                behavioral health care in the last 12 months. These are the members least likely
                to seek treatment on their own. {eligibleConnected.toLocaleString()} of them have
                initiated behavioral health care during the Reema program, a {eligiblePct}%
                activation rate in the hardest-to-reach segment of your population.
              </p>
            </div>
          </ReportMockFieldFrame>
        ) : null}

        {!isWarehouse ? (
          <p className="text-sm" style={{ color: muted }}>
            Use Refresh data in the builder to load warehouse BH connection metrics.
          </p>
        ) : null}
      </SectionReveal>
    </HostedReportSectionLayout>
  );
};

export default ConnectionsBehavioralCare;
